import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import {
  Dumbbell,
  Flag,
  Flame,
  ICON_STROKE,
  Sparkles,
  Target,
  TrendingUp,
} from "@/shared/components/icons";
import { fetchProfileSummary } from "@/shared/api";
import { shortDate } from "@/shared/format";
import type { Milestone, MilestoneKind } from "@/shared/models";

const PAGE = 8;

const KIND_ICON: Record<MilestoneKind, typeof Target> = {
  started: Flag,
  drills: Dumbbell,
  leak_report: Target,
  graduation: TrendingUp,
  overall: Flame,
};

const KIND_TINT: Record<MilestoneKind, string> = {
  started: "bg-text-muted/15 text-text-muted",
  drills: "bg-accent/15 text-accent",
  leak_report: "bg-amber-500/15 text-amber-400",
  graduation: "bg-green-500/15 text-green-400",
  overall: "bg-accent/20 text-accent",
};

/** A chronological feed of earned milestones — the "accrual over time" that
 *  makes the profile feel like an asset. Hidden until at least one milestone
 *  exists. */
export function MilestoneTimeline() {
  const { data } = useQuery({ queryKey: ["profile-summary"], queryFn: fetchProfileSummary });
  const milestones = data?.milestones ?? [];
  const [visible, setVisible] = useState(PAGE);

  if (milestones.length === 0) return null;

  const shown = milestones.slice(0, visible);
  const remaining = milestones.length - shown.length;

  return (
    <section className="rounded-xl border border-border bg-surface p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles size={16} strokeWidth={ICON_STROKE} className="text-accent" />
        <h2 className="text-sm font-semibold tracking-tight text-text">Milestones</h2>
        <span className="text-xs text-text-muted/70 tabular-nums">{milestones.length}</span>
      </div>
      <ol className="space-y-2.5">
        {shown.map((m, i) => (
          <MilestoneRow key={`${m.date}-${m.kind}-${i}`} milestone={m} />
        ))}
      </ol>
      {(remaining > 0 || visible > PAGE) && (
        <div className="flex items-center gap-4 pl-9 pt-1">
          {remaining > 0 && (
            <button
              type="button"
              onClick={() => setVisible((v) => Math.min(v + PAGE, milestones.length))}
              className="text-xs font-medium text-accent hover:underline"
            >
              Show {Math.min(PAGE, remaining)} more
            </button>
          )}
          {visible > PAGE && (
            <button
              type="button"
              onClick={() => setVisible(PAGE)}
              className="text-xs text-text-muted hover:text-text transition"
            >
              Show less
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function MilestoneRow({ milestone }: { milestone: Milestone }) {
  const Icon = KIND_ICON[milestone.kind];
  const big = milestone.kind === "overall";
  return (
    <li className="flex items-start gap-3">
      <span
        className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${KIND_TINT[milestone.kind]}`}
      >
        <Icon size={14} strokeWidth={ICON_STROKE} />
      </span>
      <div className="min-w-0 flex-1">
        <div className={`text-sm text-text${big ? " font-semibold" : ""}`}>{milestone.title}</div>
        {milestone.detail && (
          <div className="text-xs text-text-muted">{milestone.detail}</div>
        )}
      </div>
      <span className="text-xs text-text-muted/70 tabular-nums flex-shrink-0 mt-0.5">
        {shortDate(milestone.date)}
      </span>
    </li>
  );
}
